import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { SiteNav } from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';

const API = process.env.REACT_APP_BACKEND_URL;
const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';
const CREAM = '#FDFBF7';

const Switch = ({ on, onClick, label, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={onClick}
    className="relative h-6 w-11 rounded-full flex-shrink-0 transition-colors"
    style={{ background: on ? '#3CA96C' : 'rgba(42,30,92,0.18)' }}
    data-testid={testid}
  >
    <span
      className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform"
      style={{ left: 2, transform: on ? 'translateX(20px)' : 'translateX(0)' }}
    />
  </button>
);

export default function EmailPreferencesPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [view, setView] = useState(token ? 'loading' : 'no_token');
  const [email, setEmail] = useState('');
  const [prefs, setPrefs] = useState(null);
  const [categories, setCategories] = useState([]);
  const [askEmail, setAskEmail] = useState('');
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    document.title = 'Email preferences · Payday Relay';
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/email-preferences?token=${encodeURIComponent(token)}`);
        if (!res.ok) { setView('bad_token'); return; }
        const d = await res.json();
        setEmail(d.email);
        setPrefs(d.preferences);
        setCategories(d.categories || []);
        setView('ready');
      } catch {
        setView('bad_token');
      }
    })();
  }, [token]);

  const save = async (key, next) => {
    const before = prefs;
    setPrefs({ ...prefs, [key]: next });
    try {
      const res = await fetch(`${API}/api/email-preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, [key]: next }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      setPrefs(d.preferences);
      toast.success('Saved.');
    } catch {
      setPrefs(before);
      toast.error("Couldn't save that, try again.");
    }
  };

  const requestLink = async (e) => {
    e.preventDefault();
    setAsking(true);
    try {
      await fetch(`${API}/api/email-preferences/request-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: askEmail }),
      });
      toast.success('If that address is on our list, the link is on its way.');
      setAskEmail('');
    } catch {
      toast.error('That did not go through, try again.');
    } finally {
      setAsking(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: CREAM }} data-testid="email-prefs-page">
      <SiteNav />
      <main className="flex-1 px-5 sm:px-8 py-14 sm:py-20">
        <div className="max-w-xl mx-auto">
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: '#B8860B' }}>Your inbox</p>
          <h1 className="text-3xl sm:text-4xl leading-tight mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="email-prefs-headline">
            Email preferences
          </h1>

          {view === 'loading' && <p className="text-base" style={{ color: INK2 }} data-testid="email-prefs-loading">Loading…</p>}

          {(view === 'no_token' || view === 'bad_token') && (
            <div data-testid="email-prefs-request">
              <p className="text-base leading-relaxed mb-6" style={{ color: INK2 }}>
                {view === 'bad_token'
                  ? "That preferences link isn't valid anymore. Pop your email in and we'll send a fresh one."
                  : "Enter the email address we write to and we'll send you your private preferences link."}
              </p>
              <form onSubmit={requestLink} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  required
                  value={askEmail}
                  onChange={(e) => setAskEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="flex-1 px-5 py-3.5 rounded-full text-base focus:outline-none"
                  style={{ background: '#FFFFFF', color: INK, border: '1px solid rgba(42,30,92,0.15)' }}
                  data-testid="email-prefs-email-input"
                />
                <button
                  type="submit"
                  disabled={asking}
                  className="px-6 py-3.5 rounded-full font-bold text-sm disabled:opacity-60"
                  style={{ background: GOLD, color: INK }}
                  data-testid="email-prefs-request-btn"
                >
                  {asking ? 'Sending…' : 'Send my link'}
                </button>
              </form>
              <p className="text-sm mt-6" style={{ color: INK2 }}>
                Already signed in? Manage everything from{' '}
                <Link to="/app/settings" className="underline font-semibold" style={{ color: INK }} data-testid="email-prefs-settings-link">
                  your settings
                </Link>.
              </p>
            </div>
          )}

          {view === 'ready' && prefs && (
            <div data-testid="email-prefs-form">
              <p className="text-base leading-relaxed mb-8" style={{ color: INK2 }}>
                Choosing for <span className="font-semibold" style={{ color: INK }} data-testid="email-prefs-email">{email}</span>.
                Billing and account notices are always sent, they&rsquo;re the receipts for money that moves.
              </p>

              {categories.map((c) => (
                <div key={c.key} className="flex items-start justify-between gap-5 py-4 border-t" style={{ borderColor: 'rgba(42,30,92,0.12)' }}>
                  <div>
                    <p className="text-sm font-bold" style={{ color: INK }}>{c.title}</p>
                    <p className="text-xs leading-relaxed" style={{ color: INK2 }}>{c.description}</p>
                  </div>
                  <Switch
                    on={!prefs.unsubscribed_all && !!prefs[c.key]}
                    onClick={() => save(c.key, !prefs[c.key])}
                    label={c.title}
                    testid={`email-prefs-${c.key.replace('_', '-')}-toggle`}
                  />
                </div>
              ))}

              <div className="flex items-start justify-between gap-5 py-4 border-t" style={{ borderColor: 'rgba(42,30,92,0.12)' }}>
                <div>
                  <p className="text-sm font-bold" style={{ color: INK }}>Unsubscribe from all updates</p>
                  <p className="text-xs leading-relaxed" style={{ color: INK2 }}>
                    Turns off everything above in one move.
                  </p>
                </div>
                <Switch
                  on={!!prefs.unsubscribed_all}
                  onClick={() => save('unsubscribed_all', !prefs.unsubscribed_all)}
                  label="Unsubscribe from all updates"
                  testid="email-prefs-unsubscribe-all-toggle"
                />
              </div>

              <div className="flex items-start justify-between gap-5 py-4 border-t" style={{ borderColor: 'rgba(42,30,92,0.12)' }}>
                <div>
                  <p className="text-sm font-bold" style={{ color: INK }}>Contribute to research (anonymous)</p>
                  <p className="text-xs leading-relaxed" style={{ color: INK2 }}>
                    Let your race count towards research at the intersection of independent work,
                    motivation and money. No name, no email, no exact figures, only bands.
                  </p>
                </div>
                <Switch
                  on={!!prefs.research_opt_in}
                  onClick={() => save('research_opt_in', !prefs.research_opt_in)}
                  label="Contribute to research"
                  testid="email-prefs-research-toggle"
                />
              </div>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
